import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { HeartIcon, BookmarkIcon, PhoneIcon, MapPinIcon, AcademicCapIcon, BriefcaseIcon } from '@heroicons/react/24/outline';

const PLACEHOLDER = '/assets/no-photo.svg';
const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

export default function ProfileViewPage() {
  const { id } = useParams();
  const [profile,        setProfile]        = useState(null);
  const [canViewContact, setCanViewContact] = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [activePhoto,    setActivePhoto]    = useState(0);
  const [interestStatus, setInterestStatus] = useState(null);
  const [isShortlisted,  setIsShortlisted]  = useState(false);

  useEffect(() => {
    api.get(`/profiles/${id}`)
      .then(({ data }) => {
        setProfile(data.profile);
        setCanViewContact(data.canViewContact);
        setInterestStatus(data.interestStatus || null);
        setIsShortlisted(!!data.isShortlisted);
      })
      .catch(() => toast.error('Profile not found'))
      .finally(() => setLoading(false));
  }, [id]);

  const sendInterest = async () => {
    if (interestStatus) return;
    try {
      await api.post(`/interests/${id}`);
      setInterestStatus('pending');
      toast.success('💌 Interest sent!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const shortlist = async () => {
    try {
      if (isShortlisted) {
        await api.delete(`/shortlists/${id}`);
        setIsShortlisted(false);
        toast('Removed from shortlist');
      } else {
        await api.post(`/shortlists/${id}`);
        setIsShortlisted(true);
        toast.success('⭐ Shortlisted!');
      }
    } catch (err) {
      toast.error('Failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center py-20">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="text-center py-20">
          <p className="text-5xl mb-4">😔</p>
          <p className="text-gray-500">Profile not found or has been hidden.</p>
          <Link to="/search" className="btn-primary mt-4 inline-block">Back to Search</Link>
        </div>
      </div>
    );
  }

  const photos = profile.photos?.length
    ? profile.photos.map(url => url.startsWith('http') ? url : `${API_BASE}${url}`)
    : [PLACEHOLDER];

  const Section = ({ title, children }) => (
    <div className="card p-5 mb-4">
      <h3 className="font-heading font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-100 flex items-center gap-2">
        <span className="w-1 h-4 bg-primary rounded-full" />{title}
      </h3>
      {children}
    </div>
  );

  const Row = ({ label, value }) => value ? (
    <div className="flex gap-2 py-1">
      <span className="text-gray-400 text-sm w-36 flex-shrink-0">{label}</span>
      <span className="text-gray-800 text-sm font-medium capitalize">{String(value).replace(/_/g, ' ')}</span>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-6">

          {/* Left — Photos & Actions */}
          <div className="lg:col-span-1">
            {/* Photos */}
            <div className="card overflow-hidden mb-4">
              <img src={photos[activePhoto] || PLACEHOLDER} alt={profile.first_name}
                className="w-full h-80 object-cover"
                onError={e => { e.target.src = PLACEHOLDER; }} />
              {photos.length > 1 && (
                <div className="p-3 flex gap-2 overflow-x-auto">
                  {photos.map((ph, i) => (
                    <img key={i} src={ph} alt="" onClick={() => setActivePhoto(i)}
                      className={`w-12 h-12 object-cover rounded-lg cursor-pointer border-2 flex-shrink-0 ${i === activePhoto ? 'border-primary' : 'border-transparent'}`}
                      onError={e => { e.target.src = PLACEHOLDER; }} />
                  ))}
                </div>
              )}
            </div>

            {/* Profile ID */}
            <div className="text-center mb-4">
              <p className="font-heading text-xl font-bold text-gray-900">
                {profile.first_name} {profile.last_name}
              </p>
              <p className="text-sm text-gray-400 font-mono">{profile.profile_id}</p>
              {profile.is_premium && <span className="badge-premium mt-1">⭐ Premium</span>}
            </div>

            {/* Action buttons */}
            <div className="space-y-2">
              <button
                onClick={sendInterest}
                disabled={!!interestStatus}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all ${
                  interestStatus ? 'bg-red-50 text-red-500 cursor-default' : 'btn-primary'
                }`}
              >
                <HeartIcon className="w-5 h-5" />
                {interestStatus === 'accepted' ? 'Interest Accepted'
                  : interestStatus === 'declined' ? 'Interest Declined'
                  : interestStatus === 'pending' ? 'Interest Already Sent'
                  : 'Send Interest'}
              </button>
              <button onClick={shortlist} className="btn-outline w-full flex items-center justify-center gap-2 py-2.5">
                <BookmarkIcon className="w-5 h-5" /> {isShortlisted ? 'Shortlisted ✓' : 'Shortlist'}
              </button>
              {canViewContact ? (
                <div className="card p-3 bg-green-50 border-green-200 text-center">
                  <p className="text-xs text-gray-500 mb-1">Contact Number</p>
                  <p className="font-bold text-green-700 flex items-center justify-center gap-1">
                    <PhoneIcon className="w-4 h-4" /> {profile.mobile || 'Not provided'}
                  </p>
                </div>
              ) : (
                <Link to="/subscription"
                  className="block card p-3 bg-gold-50 border-gold border text-center text-sm text-gold font-medium hover:shadow-md transition-shadow">
                  🔒 Upgrade to view contact →
                </Link>
              )}
            </div>
          </div>

          {/* Right — Details */}
          <div className="lg:col-span-2 space-y-4">
            {/* Quick info */}
            <div className="card p-5">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: '🎂', label: `${profile.age} years old` },
                  { icon: '📏', label: profile.height_cm ? `${profile.height_cm} cm` : 'Height not set' },
                  { icon: '📍', label: `${profile.city || ''}, ${profile.district}, ${profile.state}` },
                  { icon: 'cross', label: profile.denomination?.replace(/_/g, ' ') || 'Christian' },
                ].map(({ icon, label }) => (
                  <div key={label} className="flex items-center gap-2 text-sm text-gray-600">
                    {icon === 'cross'
                      ? <img src="/cross-icon.png" alt="Cross" className="w-4 h-4 rounded object-cover" />
                      : <span>{icon}</span>}
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {profile.about_me && (
              <Section title="About Me">
                <p className="text-gray-600 text-sm leading-relaxed">{profile.about_me}</p>
              </Section>
            )}

            <Section title="Personal Details">
              <Row label="Marital Status"  value={profile.marital_status} />
              <Row label="Complexion"      value={profile.complexion} />
              <Row label="Blood Group"     value={profile.blood_group} />
              <Row label="Mother Tongue"   value="Telugu" />
            </Section>

            <Section title="Religion">
              <Row label="Denomination"     value={profile.denomination} />
              <Row label="Sub-denomination" value={profile.sub_denomination} />
            </Section>

            <Section title="Education & Career">
              <Row label="Education"  value={profile.education_level} />
              <Row label="Field"      value={profile.education_field} />
              <Row label="College"    value={profile.college} />
              <Row label="Occupation" value={profile.occupation_type} />
              <Row label="Job Title"  value={profile.job_title} />
              <Row label="Employer"   value={profile.employer} />
              <Row label="Income"     value={profile.annual_income?.replace(/_/g, ' ')} />
            </Section>

            <Section title="Family">
              <Row label="Father"     value={profile.father_name} />
              <Row label="Father's Occ" value={profile.father_occupation} />
              <Row label="Mother"     value={profile.mother_name} />
              <Row label="Brothers"   value={profile.brothers} />
              <Row label="Sisters"    value={profile.sisters} />
              <Row label="Family Type" value={profile.family_type} />
              <Row label="Family Status" value={profile.family_status} />
            </Section>

            {profile.about_partner && (
              <Section title="Partner Expectations">
                <p className="text-gray-600 text-sm leading-relaxed">{profile.about_partner}</p>
                {profile.min_age && <Row label="Age" value={`${profile.min_age} – ${profile.max_age} years`} />}
              </Section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
